import { NgClass } from '@angular/common';
import { Component, computed, inject, input, LOCALE_ID, OnInit, signal } from '@angular/core';
import { finalize, forkJoin } from 'rxjs';
import { SafeUrl } from '@angular/platform-browser';

import { ProductsFileMgt } from '../../../../../services/product/file-management/products.file-mgt.service';
import { ErrorData } from '../../../../../utils/error-data/error-data';
import { Loading } from '../../../../../utils/loading/loading';
import { ImageCarousel } from '../../../product-details/utils/image-carousel/image-carousel';
import { BundleDetailsTab } from './bundle-details-tab/bundle-details-tab';
import { BundleService } from '../../../../../services/bundle/bundle';
import { BundleDetails } from '../../../../../pages/admin-dashboard/promotions/bundles-managment/bundles.model';
import { BundleIncludedItemsTab } from './bundle-included-items-tab/bundle-included-items-tab';

@Component({
  selector: 'app-bundle-details-component',
  imports: [NgClass, ErrorData, Loading, ImageCarousel, BundleDetailsTab, BundleIncludedItemsTab],
  templateUrl: './bundle-details-component.html',
  styleUrl: './bundle-details-component.scss',
})
export class BundleDetailsComponent implements OnInit {
  private bundleService = inject(BundleService);
  private fileMgt = inject(ProductsFileMgt);
  private locale = inject(LOCALE_ID);

  isArabic = computed(() => this.locale.startsWith('ar'));
  bundleId = input.required<string>();

  bundleData = signal<BundleDetails | null>(null);
  loadingBundleData = signal(false);
  errorBundleData = signal<string | null>(null);
  bundleImageUrl = signal<{ url: SafeUrl | string; isImage: boolean } | undefined>(undefined);
  bundleImages = signal<{ url: SafeUrl | string; isImage: boolean }[]>([]);

  activeTab = signal<'details' | 'products'>('details');

  setActiveTab(tab: 'details' | 'products') {
    this.activeTab.set(tab);
  }

  ngOnInit() {
    this.loadBundleDetails();
  }

  loadBundleDetails() {
    this.loadingBundleData.set(true);
    this.bundleService
      .getBundleById(this.bundleId())
      .pipe(finalize(() => this.loadingBundleData.set(false)))
      .subscribe({
        next: (bundle) => {
          this.bundleData.set(bundle);
          this.bundleImageUrl.set({ url: '', isImage: false });

          if (bundle.profileImageFids?.length) {
            this.fetchImage(bundle.profileImageFids[0]).subscribe({
              next: (img) => this.bundleImageUrl.set(img),
              error: () => this.bundleImageUrl.set({ url: '', isImage: false }),
            });

            if (bundle.profileImageFids.length > 1) {
              const rest = bundle.profileImageFids.slice(1).map((fid) => this.fetchImage(fid));
              forkJoin(rest).subscribe({
                next: (images) => this.bundleImages.set(images as any),
                error: () => this.bundleImages.set([]),
              });
            }
          }
        },
        error: (err) => {
          this.errorBundleData.set(
            this.isArabic() ? err.messageAr || 'حدث خطأ ما' : err.messageEn || 'Failed to load bundle details.',
          );
        },
      });
  }

  private fetchImage(fid: string) {
    return this.fileMgt.getImageUrl(fid);
  }
}
